import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useTranslation } from "@/contexts/LanguageContext";
import { Images, Calendar, X, ArrowLeft, ArrowRight } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

function resolveUrl(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export default function Gallery() {
  const [albums, setAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const { lang } = useTranslation();

  useEffect(() => { axios.get(`${API}/gallery`).then(r => setAlbums(r.data)).catch(() => {}); }, []);

  const title = { es: "Galeria", en: "Gallery", fr: "Galerie", pt: "Galeria" };
  const desc = { es: "Revive los mejores momentos de nuestros eventos", en: "Relive the best moments from our events", fr: "Revivez les meilleurs moments", pt: "Reviva os melhores momentos" };
  const photos_label = { es: "fotos", en: "photos", fr: "photos", pt: "fotos" };

  const currentPhotos = selectedAlbum?.photos || [];

  return (
    <main className="pt-20 min-h-screen">
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400 mb-2">Pro Vertical</p>
          <h1 className="text-4xl sm:text-5xl tracking-tight leading-none font-black uppercase font-['Cabinet_Grotesk'] mb-4" data-testid="gallery-title">{title[lang] || title.es}</h1>
          <p className="text-base text-gray-400 max-w-lg">{desc[lang] || desc.es}</p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {!selectedAlbum ? (
            /* Album Grid */
            <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="albums-grid"
            >
              {albums.map(album => (
                <motion.div key={album.id} variants={fadeUp}
                  className="bg-[#121212] border border-white/5 overflow-hidden card-hover cursor-pointer group"
                  onClick={() => setSelectedAlbum(album)} data-testid={`album-${album.id}`}
                >
                  <div className="h-56 overflow-hidden bg-[#1A1A1A]">
                    {album.cover_image ? (
                      <img src={resolveUrl(album.cover_image)} alt={album.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : album.photos?.length > 0 ? (
                      <img src={resolveUrl(album.photos[0].url || album.photos[0])} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Images size={48} className="text-gray-600" /></div>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-semibold font-['Cabinet_Grotesk'] group-hover:text-[#FF4D00] transition-colors">{album.title}</h3>
                    {album.event_name && <p className="text-xs text-gray-400 mt-1">{album.event_name}</p>}
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-2">
                      {album.date && <span className="flex items-center gap-1"><Calendar size={12} />{album.date}</span>}
                      <span className="flex items-center gap-1"><Images size={12} />{album.photos?.length || 0} {photos_label[lang] || photos_label.es}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            /* Photo Grid for selected album */
            <div>
              <button onClick={() => setSelectedAlbum(null)} className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-[#FF4D00] mb-6" data-testid="gallery-back">
                <ArrowLeft size={14} /> {lang === "en" ? "Back to Albums" : "Volver a Albums"}
              </button>
              <h2 className="text-2xl font-black uppercase font-['Cabinet_Grotesk'] mb-2">{selectedAlbum.title}</h2>
              {selectedAlbum.event_name && <p className="text-sm text-gray-400 mb-6">{selectedAlbum.event_name} {selectedAlbum.date && `- ${selectedAlbum.date}`}</p>}

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="photos-grid">
                {currentPhotos.map((photo, idx) => {
                  const url = typeof photo === "string" ? photo : photo.url;
                  const caption = typeof photo === "object" ? photo.caption : "";
                  return (
                    <div key={idx} className="aspect-square bg-[#1A1A1A] border border-white/5 overflow-hidden cursor-pointer group"
                      onClick={() => setLightboxIdx(idx)} data-testid={`photo-${idx}`}>
                      <img src={resolveUrl(url)} alt={caption || ""} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {albums.length === 0 && !selectedAlbum && (
            <div className="text-center py-20 text-gray-400"><Images size={48} className="mx-auto mb-4 opacity-30" /><p>{lang === "en" ? "No albums yet" : "No hay albums aun"}</p></div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <Dialog open={lightboxIdx >= 0} onOpenChange={() => setLightboxIdx(-1)}>
        <DialogContent className="bg-black/95 border-white/10 max-w-4xl p-2">
          {lightboxIdx >= 0 && currentPhotos[lightboxIdx] && (
            <div className="relative">
              <img src={resolveUrl(typeof currentPhotos[lightboxIdx] === "string" ? currentPhotos[lightboxIdx] : currentPhotos[lightboxIdx].url)}
                alt="" className="w-full h-auto max-h-[80vh] object-contain" />
              {typeof currentPhotos[lightboxIdx] === "object" && currentPhotos[lightboxIdx].caption && (
                <p className="text-sm text-gray-300 text-center mt-3">{currentPhotos[lightboxIdx].caption}</p>
              )}
              <div className="absolute top-1/2 -translate-y-1/2 left-2">
                <button onClick={(e) => { e.stopPropagation(); setLightboxIdx(Math.max(0, lightboxIdx - 1)); }}
                  className="bg-black/60 text-white p-2 hover:bg-[#FF4D00]"><ArrowLeft size={20} /></button>
              </div>
              <div className="absolute top-1/2 -translate-y-1/2 right-2">
                <button onClick={(e) => { e.stopPropagation(); setLightboxIdx(Math.min(currentPhotos.length - 1, lightboxIdx + 1)); }}
                  className="bg-black/60 text-white p-2 hover:bg-[#FF4D00]"><ArrowRight size={20} /></button>
              </div>
              <p className="text-xs text-gray-500 text-center mt-2">{lightboxIdx + 1} / {currentPhotos.length}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}
